/**
 * StrengthBadge — compact, colour-coded signal-strength indicator.
 *
 * Accepts the RockBand-style string strength ('HIGH' | 'MEDIUM' | 'LOW') OR a
 * legacy numeric signalStrength (3/2/1) and renders coloured stars with a
 * tooltip. Falls back to a muted "—" when no strength is known. Shared by the
 * Dashboard Recent/Parked Alerts and the Alerts page.
 */
import React from 'react';
import { Box, Tooltip } from '@mui/material';

type Tier = 'HIGH' | 'MEDIUM' | 'LOW';

function tierOf(s: string | number | null | undefined): Tier | null {
  if (s == null || s === '') return null;
  if (typeof s === 'number') return s >= 3 ? 'HIGH' : s === 2 ? 'MEDIUM' : s >= 1 ? 'LOW' : null;
  const u = String(s).toUpperCase();
  if (u === 'HIGH' || u === 'H') return 'HIGH';
  if (u === 'MEDIUM' || u === 'MED' || u === 'M') return 'MEDIUM';
  if (u === 'LOW' || u === 'L') return 'LOW';
  return null;
}

const META: Record<Tier, { stars: string; label: string; color: string }> = {
  HIGH:   { stars: '★★★', label: 'High',   color: 'success.main' },
  MEDIUM: { stars: '★★',  label: 'Medium', color: 'warning.main' },
  LOW:    { stars: '★',   label: 'Low',    color: 'text.secondary' },
};

interface Props {
  strength?: string | number | null;
  /** Render the word ("High") instead of stars. */
  text?: boolean;
  fontSize?: string;
}

const StrengthBadge: React.FC<Props> = ({ strength, text, fontSize = '0.8rem' }) => {
  const tier = tierOf(strength);
  if (!tier) return <Box component="span" sx={{ color: 'text.disabled', fontSize }}>—</Box>;
  const m = META[tier];
  return (
    <Tooltip title={`Signal strength: ${m.label}`} arrow>
      <Box component="span" sx={{ color: m.color, fontSize, fontWeight: 700, letterSpacing: text ? 0 : '1px', cursor: 'default', whiteSpace: 'nowrap' }}>
        {text ? m.label : m.stars}
      </Box>
    </Tooltip>
  );
};

export default StrengthBadge;
